import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { StatusBadge } from '@/components/common/StatusBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDate } from '@/lib/utils';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import {
  Hash, Building2, CheckCircle, XCircle,
  Plus, Edit, ArrowLeftRight, Send, Shield, RefreshCw,
  MoreHorizontal, Hash as HashIcon, Cake,
  MessageSquare, MessageSquareOff, Loader2, Phone, MailCheck,
  Copy, Check, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentAPI } from '@/lib/api';

/** Copy helper with an execCommand fallback for when the async Clipboard
 *  API is unavailable (non-secure context, denied permission, etc.). */
const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

/** Small pill that copies a value to the clipboard with inline feedback. */
const CopyChip = ({ icon: Icon, label, value, testid, tone = 'slate' }) => {
  const [done, setDone] = React.useState(false);
  if (!value) return null;
  const tones = {
    slate: 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200',
    navy: 'bg-sky-50 text-sky-700 hover:bg-sky-100 border-sky-200',
  };
  const onClick = async (e) => {
    e.stopPropagation();
    const ok = await copyText(value);
    if (ok) {
      setDone(true);
      toast.success(`${label} copied`);
      setTimeout(() => setDone(false), 1500);
    } else {
      toast.error('Could not copy');
    }
  };
  return (
    <button
      type="button" onClick={onClick} data-testid={testid} title={`Copy ${label}`}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border transition-colors ${tones[tone]}`}
    >
      {done ? <Check size={12} className="text-emerald-600" /> : <Icon size={12} />} {label}
    </button>
  );
};

// Phase 27.14 — Timezone-safe birthday check.
//
// Bug we are fixing: `new Date("1990-05-14")` is parsed by JS as UTC
// midnight. In America/New_York (KR HQ) that reads as `1990-05-13T20:00`
// — so the birthday banner appeared 4h early and lingered into the wrong
// day. We now parse the stored `YYYY-MM-DD` string directly and compare
// only month + day against the operator's LOCAL calendar day. No Date
// object is used for the comparison itself.
const isBirthdayToday = (dob) => {
  if (!dob || typeof dob !== 'string') return false;
  const match = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return false;
  const [, , mm, dd] = match;
  const now = new Date();
  const localMM = String(now.getMonth() + 1).padStart(2, '0');
  const localDD = String(now.getDate()).padStart(2, '0');
  return mm === localMM && dd === localDD;
};

/**
 * AgentHeader — identity + actions strip on the Agent Command Center.
 *
 * Phase 17.5 UX:
 *   • Primary action (Add Contract) stays visible as a solid button.
 *   • Secondary actions collapse under a "More Actions" dropdown so the
 *     header stays clean above the fold.
 */
export const AgentHeader = ({
  agent,
  agency,
  linkedUser,
  isKR = false,
  onEdit,
  onTransfer,
  onAddContract,
  onInvite,
  onManageUser,
  onToggleUserStatus,
  onChangeNpn,
  onRegenerateAccess,
  onUpdateInfo,
  onPhotoChanged,
  onLeadSmsChanged,
}) => {
  // Phase 27.37.N — Inline quick-toggle for the Lead SMS permission.
  // KR admins can flip this without opening the Edit dialog. Hooks
  // must run before the early-return below to satisfy rules-of-hooks.
  const [savingLeadSms, setSavingLeadSms] = React.useState(false);

  if (!agent) return null;

  const canSmsLeads = !!agent.can_sms_leads;
  const toggleLeadSms = async (e) => {
    e.stopPropagation();
    setSavingLeadSms(true);
    try {
      await agentAPI.update(agent.npn, { can_sms_leads: !canSmsLeads });
      toast.success(
        !canSmsLeads
          ? 'SMS a Leads habilitado para este agente'
          : 'SMS a Leads deshabilitado para este agente',
      );
      onLeadSmsChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cambiar el permiso');
    } finally {
      setSavingLeadSms(false);
    }
  };

  // "SMS Active" — org policy: agents are SMS-active BY DEFAULT. Active when
  // there's a mobile on file (agent or linked user) and the agent hasn't
  // explicitly opted out. No self opt-in required.
  const smsInternalActive = !!(
    (agent?.phone || agent?.mobile || linkedUser?.phone || linkedUser?.mobile || linkedUser?.cell) &&
    !(linkedUser && linkedUser.sms_transactional_opt_out)
  );

  return (
    <Card className="mb-6" data-testid="agent-overview-card">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <AgentAvatar
              agent={agent}
              size="w-16 h-16"
              rounded="lg"
              textSize="text-xl"
              editable={isKR}
              onChanged={onPhotoChanged}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2
                  className="text-2xl font-heading font-semibold"
                  style={{ color: 'var(--kr-navy-900)' }}
                  data-testid="agent-name"
                >
                  {agent.first_name} {agent.last_name}
                </h2>
                {(agent.phone || agent.mobile) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.krSoftphoneDial) window.krSoftphoneDial(agent.phone || agent.mobile, { contact_type: 'agent', contact_id: agent.npn });
                      else toast.error('Softphone unavailable');
                    }}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                    title="Call via softphone"
                    data-testid="agent-call-btn"
                  >
                    <Phone size={12} /> Call
                  </button>
                )}
                {isBirthdayToday(agent.date_of_birth) && (
                  <span
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-gradient-to-r from-pink-100 to-amber-100 text-pink-800 border border-pink-200 animate-pulse"
                    title={`Wishing ${agent.first_name} a fantastic birthday`}
                    data-testid="birthday-badge"
                  >
                    <Cake size={13} />
                    Happy Birthday!
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <Hash size={14} />
                  <span className="font-mono" data-testid="agent-npn">NPN: {agent.npn}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Building2 size={14} />
                  <Link
                    to={`/agencies/${agency?.agency_id}`}
                    className="hover:underline"
                    style={{ color: 'var(--kr-navy-700)' }}
                    data-testid="agent-agency-link"
                  >
                    {agency?.agency_name || 'Unknown Agency'}
                  </Link>
                </span>
                <StatusBadge status={agent.agent_status} />
                {linkedUser && (
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                      linkedUser.status === 'active'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-red-50 text-red-700'
                    }`}
                    data-testid="user-access-badge"
                  >
                    {linkedUser.status === 'active' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    Portal {linkedUser.status === 'active' ? 'Active' : 'Disabled'}
                  </span>
                )}
                {/* Phase 27.37.P — Internal SMS opt-in badge. */}
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                    smsInternalActive
                      ? 'bg-sky-50 text-sky-700'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                  data-testid="sms-active-badge"
                  title={
                    smsInternalActive
                      ? 'Recibe SMS internos (eventos, calendario, broadcasts)'
                      : 'Sin opt-in de SMS interno'
                  }
                >
                  {smsInternalActive
                    ? <MessageSquare size={12} />
                    : <MessageSquareOff size={12} />}
                  SMS {smsInternalActive ? 'Active' : 'Inactive'}
                </span>
                {/* Phase 27.37.P — SMS-to-Leads permission badge with
                    KR-only quick toggle. Click flips can_sms_leads. */}
                {isKR ? (
                  <button
                    type="button"
                    onClick={toggleLeadSms}
                    disabled={savingLeadSms}
                    data-testid="sms-leads-badge-toggle"
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full transition-colors ${
                      canSmsLeads
                        ? 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200'
                    } disabled:opacity-50`}
                    title={
                      canSmsLeads
                        ? 'Click para deshabilitar el envío de SMS a Leads'
                        : 'Click para habilitar el envío de SMS a Leads'
                    }
                  >
                    {savingLeadSms
                      ? <Loader2 size={12} className="animate-spin" />
                      : (canSmsLeads
                          ? <MessageSquare size={12} />
                          : <MessageSquareOff size={12} />)}
                    SMS Leads {canSmsLeads ? 'Active' : 'Off'}
                  </button>
                ) : (
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                      canSmsLeads
                        ? 'bg-violet-50 text-violet-700'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                    data-testid="sms-leads-badge"
                  >
                    {canSmsLeads
                      ? <MessageSquare size={12} />
                      : <MessageSquareOff size={12} />}
                    SMS Leads {canSmsLeads ? 'Active' : 'Off'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="agent-copy-toolbar">
                <CopyChip icon={Mail} label="Copy Email" value={agent.email} testid="copy-agent-email" tone="navy" />
                <CopyChip icon={HashIcon} label="Copy NPN" value={agent.npn} testid="copy-agent-npn" />
                <CopyChip
                  icon={Copy}
                  label="Copy All Info"
                  value={[
                    `Agent Name: ${`${agent.first_name || ''} ${agent.last_name || ''}`.trim()}`,
                    `NPN: ${agent.npn || ''}`,
                    `Email: ${agent.email || ''}`,
                    `Phone: ${agent.mobile_phone || agent.phone || agent.mobile || ''}`,
                  ].join('\n')}
                  testid="copy-agent-all"
                />
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                <span>Created: {formatDate(agent.created_at)}</span>
              </div>
            </div>
          </div>

          {isKR && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="agent-actions">
              {/* Primary action — always visible */}
              <Button size="sm" onClick={onAddContract} data-testid="add-contract-btn">
                <Plus size={14} className="mr-1" /> Add Contract
              </Button>

              {/* Secondary actions collapsed inside a single menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="more-actions-btn">
                    <MoreHorizontal size={14} className="mr-1" /> More Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={onEdit} data-testid="edit-agent-btn">
                    <Edit size={14} className="mr-2" /> Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onTransfer} data-testid="transfer-agent-btn">
                    <ArrowLeftRight size={14} className="mr-2" /> Transfer Agency
                  </DropdownMenuItem>
                  {onChangeNpn && (
                    <DropdownMenuItem onClick={onChangeNpn} data-testid="change-npn-btn">
                      <HashIcon size={14} className="mr-2" /> Change NPN
                    </DropdownMenuItem>
                  )}
                  {onUpdateInfo && (
                    <DropdownMenuItem onClick={onUpdateInfo} data-testid="update-info-btn">
                      <MailCheck size={14} className="mr-2" /> Update Agent Info
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {linkedUser ? (
                    <>
                      <DropdownMenuItem onClick={onManageUser} data-testid="manage-user-btn">
                        <Shield size={14} className="mr-2" /> Manage User
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={onToggleUserStatus}
                        className={linkedUser.status === 'active' ? 'text-red-600 focus:text-red-700' : 'text-emerald-600 focus:text-emerald-700'}
                        data-testid={linkedUser.status === 'active' ? 'disable-user-btn' : 'enable-user-btn'}
                      >
                        {linkedUser.status === 'active'
                          ? <><XCircle size={14} className="mr-2" /> Disable Access</>
                          : <><CheckCircle size={14} className="mr-2" /> Enable Access</>}
                      </DropdownMenuItem>
                      {onRegenerateAccess && (
                        <DropdownMenuItem
                          onClick={onRegenerateAccess}
                          className="text-amber-700 focus:text-amber-800"
                          data-testid="regenerate-access-menu-btn"
                        >
                          <RefreshCw size={14} className="mr-2" /> Regenerate Access
                        </DropdownMenuItem>
                      )}
                    </>
                  ) : (
                    <DropdownMenuItem onClick={onInvite} data-testid="send-invite-btn">
                      <Send size={14} className="mr-2" /> Generate Access
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AgentHeader;
